import React from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Link from "@mui/material/Link";
import { Trans, useTranslation } from "react-i18next";
import { useBrandingContext } from "context/BrandingContext";

/**
 * The fallback matches i18next's own `supportEmail` default, so the link and
 * the address the agreement text renders cannot disagree.
 */
const SUPPORT_EMAIL = "support@outpave.com";

interface AutoBalanceAuthorizationDialogProps {
  open: boolean;
  /** The authorization was given: the caller ticks its own box. */
  onAccept: () => void;
  /** Dismissed without accepting, so the box stays clear. */
  onClose: () => void;
}

/**
 * The ACH authorization an admin agrees to before Auto Balance can debit their
 * bank. It gates the confirm checkbox rather than sitting beside it: the box is
 * the record that this was accepted, so it may only be ticked from here.
 *
 * A plain themed `Dialog` rather than the package's `ConfirmDialog`, which
 * always renders a Cancel button beside its confirm; an agreement has one
 * action, and dismissing is what declining looks like.
 */
const AutoBalanceAuthorizationDialog: React.FC<
  AutoBalanceAuthorizationDialogProps
> = ({ open, onAccept, onClose }) => {
  const { t } = useTranslation();
  const { platform } = useBrandingContext();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm">
      <DialogTitle>{t("Auto Balance Authorization Agreement")}</DialogTitle>

      <DialogContent>
        <DialogContentText>
          <Trans
            i18nKey="AutoBalanceAuthorizationAgreement"
            components={{
              mail: (
                <Link href={`mailto:${platform?.email || SUPPORT_EMAIL}`} />
              ),
            }}
          />
        </DialogContentText>
      </DialogContent>

      <DialogActions>
        <Button variant="primary-filled" onClick={onAccept}>
          {t("Accept")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AutoBalanceAuthorizationDialog;
